import { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { X, Search, Link2, Copy, Trash2, Check } from "lucide-react";
import { toast } from "react-hot-toast";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 520px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  @media (max-width: 900px) {
    width: 100vw;
    max-width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #6b7280;
  &:hover {
    background-color: #f3f4f6;
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
`;

const Tab = styled.button`
  flex: 1;
  padding: 12px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  background: none;
  cursor: pointer;
  color: ${props => props.$active ? '#2563eb' : '#6b7280'};
  border-bottom: 2px solid ${props => props.$active ? '#2563eb' : 'transparent'};
  transition: all 0.15s;

  &:hover {
    color: #2563eb;
    background-color: #f9fafb;
  }
`;

const Body = styled.div`
  padding: 20px 24px;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 36px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
`;

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #111827;
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const PermissionSelect = styled.select`
  padding: 4px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  background: white;
`;

const InviteButton = styled.button`
  padding: 6px 14px;
  background-color: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background-color: #1d4ed8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: #e5e7eb;
  margin: 16px 0;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const ShareItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #ef4444;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background-color: #fef2f2;
  }
`;

const LinkSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LinkInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
  background-color: #f9fafb;
  outline: none;
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 13px;
  color: #374151;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const CreateLinkButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background-color: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #1d4ed8;
  }
`;

const LinkItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background-color: #f9fafb;
  border-radius: 8px;
  margin-bottom: 8px;
`;

const LinkMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background-color: ${props => props.$permission === 'edit' ? '#dbeafe' : '#f3e8ff'};
  color: ${props => props.$permission === 'edit' ? '#2563eb' : '#7c3aed'};
`;

export default function ShareModal({ isOpen, onClose, chartId }) {
  const [activeTab, setActiveTab] = useState("invite"); // invite | link
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [shares, setShares] = useState([]);
  const [links, setLinks] = useState([]);
  const [invitePermission, setInvitePermission] = useState("edit");
  const [linkPermission, setLinkPermission] = useState("view");
  const [copiedId, setCopiedId] = useState(null);

  // 공유 목록 가져오기
  const fetchShares = useCallback(async () => {
    try {
      const res = await fetch(`/request/gantt-chart/${chartId}/shares`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) setShares(data.shares);
    } catch (e) {
      console.error('공유 목록 조회 실패:', e);
    }
  }, [chartId]);

  // 링크 목록 가져오기
  const fetchLinks = useCallback(async () => {
    try {
      const res = await fetch(`/request/gantt-chart/${chartId}/share-links`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) setLinks(data.links);
    } catch (e) {
      console.error('링크 목록 조회 실패:', e);
    }
  }, [chartId]);

  useEffect(() => {
    if (isOpen && chartId) {
      fetchShares();
      fetchLinks();
    }
  }, [isOpen, chartId, fetchShares, fetchLinks]);

  // 유저 검색
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/request/search-users?q=${encodeURIComponent(searchQuery)}`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (data.success) {
          // 이미 공유된 유저 제외
          const sharedIds = shares.map(s => s.user_id);
          setSearchResults(data.users.filter(u => !sharedIds.includes(u.user_id)));
        }
      } catch (e) {
        console.error('유저 검색 실패:', e);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, shares]);

  // 유저 초대
  const handleInvite = async (targetUserId) => {
    try {
      const res = await fetch(`/request/gantt-chart/${chartId}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sharedWithUserId: targetUserId, permission: invitePermission }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('초대되었습니다');
        setSearchQuery("");
        setSearchResults([]);
        fetchShares();
      } else {
        toast.error(data.message);
      }
    } catch (e) {
      toast.error('초대 실패');
    }
  };

  // 공유 삭제
  const handleRemoveShare = async (shareId) => {
    try {
      const res = await fetch(`/request/gantt-chart/${chartId}/share/${shareId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        toast.success('공유가 해제되었습니다');
        fetchShares();
      }
    } catch (e) {
      toast.error('공유 해제 실패');
    }
  };

  // 공유 링크 생성
  const handleCreateLink = async () => {
    try {
      const res = await fetch(`/request/gantt-chart/${chartId}/share-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ permission: linkPermission }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('공유 링크가 생성되었습니다');
        fetchLinks();
      }
    } catch (e) {
      toast.error('링크 생성 실패');
    }
  };

  // 링크 비활성화
  const handleDeactivateLink = async (linkId) => {
    try {
      const res = await fetch(`/request/gantt-chart/${chartId}/share-link/${linkId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        toast.success('링크가 비활성화되었습니다');
        fetchLinks();
      }
    } catch (e) {
      toast.error('링크 비활성화 실패');
    }
  };

  // 클립보드 복사
  const handleCopyLink = (token, id) => {
    const url = `${window.location.origin}/chart/${token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success('링크가 복사되었습니다');
    });
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <Title>팀 멤버 초대</Title>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <TabContainer>
          <Tab $active={activeTab === "invite"} onClick={() => setActiveTab("invite")}>
            유저 초대
          </Tab>
          <Tab $active={activeTab === "link"} onClick={() => setActiveTab("link")}>
            공유 링크
          </Tab>
        </TabContainer>

        <Body>
          {activeTab === "invite" && (
            <>
              <SearchContainer>
                <SearchIcon>
                  <Search size={16} />
                </SearchIcon>
                <SearchInput
                  placeholder="이름, 계정, 이메일로 검색..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </SearchContainer>

              {searchResults.length > 0 && (
                <UserList>
                  {searchResults.map(u => (
                    <UserItem key={u.user_id}>
                      <UserInfo>
                        <Avatar>
                          {u.profile_image ? (
                            <img src={u.profile_image} alt={u.user_name} />
                          ) : (
                            u.user_name?.slice(0, 2)
                          )}
                        </Avatar>
                        <div>
                          <UserName>{u.user_name}</UserName>
                          <UserEmail>{u.user_account || u.email_address}</UserEmail>
                        </div>
                      </UserInfo>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <PermissionSelect
                          value={invitePermission}
                          onChange={e => setInvitePermission(e.target.value)}
                        >
                          <option value="view">보기</option>
                          <option value="edit">편집</option>
                          <option value="admin">관리</option>
                        </PermissionSelect>
                        <InviteButton onClick={() => handleInvite(u.user_id)}>
                          초대
                        </InviteButton>
                      </div>
                    </UserItem>
                  ))}
                </UserList>
              )}

              {shares.length > 0 && (
                <>
                  <Divider />
                  <SectionTitle>공유 중인 유저 ({shares.length}명)</SectionTitle>
                  {shares.map(s => (
                    <ShareItem key={s.id}>
                      <UserInfo>
                        <Avatar>
                          {s.profile_image ? (
                            <img src={s.profile_image} alt={s.user_name} />
                          ) : (
                            s.user_name?.slice(0, 2)
                          )}
                        </Avatar>
                        <div>
                          <UserName>{s.user_name}</UserName>
                          <UserEmail>
                            {s.user_account} · <Badge $permission={s.permission}>{s.permission}</Badge>
                          </UserEmail>
                        </div>
                      </UserInfo>
                      <RemoveButton onClick={() => handleRemoveShare(s.id)}>
                        <Trash2 size={16} />
                      </RemoveButton>
                    </ShareItem>
                  ))}
                </>
              )}
            </>
          )}

          {activeTab === "link" && (
            <LinkSection>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <PermissionSelect
                  value={linkPermission}
                  onChange={e => setLinkPermission(e.target.value)}
                  style={{ padding: '8px 12px', fontSize: '14px' }}
                >
                  <option value="view">보기 전용</option>
                  <option value="edit">편집 가능</option>
                </PermissionSelect>
                <CreateLinkButton onClick={handleCreateLink}>
                  <Link2 size={16} />
                  새 링크 생성
                </CreateLinkButton>
              </div>

              {links.filter(l => l.is_active).map(link => (
                <LinkItem key={link.id}>
                  <div>
                    <LinkRow>
                      <LinkInput
                        readOnly
                        value={`${window.location.origin}/chart/${link.token}`}
                      />
                      <CopyButton onClick={() => handleCopyLink(link.token, link.id)}>
                        {copiedId === link.id ? <Check size={14} /> : <Copy size={14} />}
                        {copiedId === link.id ? '복사됨' : '복사'}
                      </CopyButton>
                    </LinkRow>
                    <LinkMeta style={{ marginTop: '4px' }}>
                      <Badge $permission={link.permission}>{link.permission === 'edit' ? '편집' : '보기'}</Badge>
                      {' · '}
                      {new Date(link.created_at).toLocaleDateString('ko-KR')} 생성
                    </LinkMeta>
                  </div>
                  <RemoveButton onClick={() => handleDeactivateLink(link.id)}>
                    <Trash2 size={16} />
                  </RemoveButton>
                </LinkItem>
              ))}

              {links.filter(l => l.is_active).length === 0 && (
                <div style={{ textAlign: 'center', color: '#9ca3af', padding: '24px 0', fontSize: '14px' }}>
                  생성된 공유 링크가 없습니다
                </div>
              )}
            </LinkSection>
          )}
        </Body>
      </Modal>
    </Overlay>
  );
}
